/**
 * Screen 7 — DeCA detail.
 *
 * Header (id · estado · generated), the QrPanel, the four sections read-only in
 * the PDF's own order, the Historial and the version chain.
 *
 * The QR panel is the compliance-critical part of this screen: the URL it shows
 * is the one printed on the document, it downloads the PDF directly with no
 * sign-in and no landing page (BOE Third.3–4), and an in-place amendment leaves
 * it **unchanged** (Fifth.1). The portal shows it and copies it; it never wraps
 * it in a screen of its own.
 *
 * Fields changed by the most recent amendment are highlighted the way the PDF
 * highlights them, with the superseded value struck through beside them — the
 * "previous data retained and clearly marked as no longer valid" of Fifth.1,
 * visible in the portal and not only in the file.
 */

import { useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { ifMatch, unwrap, type Deca, type HistoryEntry } from '@deca/api'

import { api, downloadFile, MIME } from '../app/api.ts'
import { apiErrorMessage, isConflict, isResourceNotFound } from '../app/errors.ts'
import { useDeca, useDecaHistory, useDecaVersions, useInvalidateDeca } from '../app/queries.ts'
import { actorText } from '../deca/actor.ts'
import { useAuth } from '../app/auth.tsx'
import { useOrganisationChoice } from '../app/useOrganisationChoice.ts'
import { abilitiesFor } from '../deca/roles.ts'
import { offeredFamilies } from '../deca/transportType.ts'
import { hasDeliveryConfirmation } from '../deca/delivery.ts'
import { consignmentDatesDiffer, consignmentServiceDate } from '../deca/consignmentDates.ts'
import {
  authenticatedDownloadAvailable,
  canComplete,
  canDeleteDraft,
  canAmend,
  canVoid,
  directDownloadLive,
  downloadWindowDays,
  isGenerated,
} from '../deca/estado.ts'
import { fieldsForSection } from '../deca/fields.ts'
import { formatDateTime, formatHistoryValue, formatQuantity, formatServiceDate } from '../deca/format.ts'
import { AppShell } from '../shell/AppShell.tsx'
import {
  Button,
  Card,
  Datum,
  EmptyState,
  ErrorState,
  LoadingRows,
  Notice,
  SectionNumber,
} from '../ui/primitives.tsx'
import { DeliveryPanel } from '../ui/DeliveryPanel.tsx'
import { DecaEmailsPanel } from '../ui/DecaEmailsPanel.tsx'
import { EmailSendDialog } from '../ui/EmailSendDialog.tsx'
import { canSendByEmail } from '../deca/decaEmails.ts'
import { Dialog } from '../ui/Dialog.tsx'
import { DocumentOwnerLine } from '../ui/DocumentOwnerLine.tsx'
import { EstadoChip, GroupedTag } from '../ui/EstadoChip.tsx'
import { Icon } from '../ui/Icon.tsx'
import { QrPanel } from '../ui/QrPanel.tsx'
import { RowMenu } from '../ui/RowMenu.tsx'
import { TextAreaInput } from '../ui/form.tsx'
import { useToast } from '../ui/Toast.tsx'
import { ModifyDialog } from './ModifyDialog.tsx'

export function DecaDetailScreen() {
  const { t, i18n } = useTranslation()
  const { decaId } = useParams({ strict: false }) as { decaId: string }
  const navigate = useNavigate()
  const toast = useToast()
  const invalidate = useInvalidateDeca()
  const { principal } = useAuth()
  const abilities = abilitiesFor(principal)
  const { organisation: context } = useOrganisationChoice()

  const query = useDeca(decaId)
  const deca = query.data?.data
  const etag = query.data?.etag ?? null

  const history = useDecaHistory(decaId, Boolean(deca && isGenerated(deca)))
  const versions = useDecaVersions(decaId, Boolean(deca && isGenerated(deca)))

  const [modifyOpen, setModifyOpen] = useState(false)
  const [voidOpen, setVoidOpen] = useState(false)
  const [sendOpen, setSendOpen] = useState(false)
  const [busy, setBusy] = useState(false)
  const [actionError, setActionError] = useState<string | null>(null)

  /** The newest version, for the "Modificado por" line in Detalles. */
  const lastVersion = useMemo(() => {
    const all = versions.data?.data ?? []
    return all.reduce<(typeof all)[number] | undefined>(
      (best, version) => (best && best.versionNo >= version.versionNo ? best : version),
      undefined,
    )
  }, [versions.data])

  /**
   * Columns touched by the newest version — the PDF's yellow highlight. A consignment's own
   * *observaciones* is shown in its block, apart from the document's in section 4, so its changes
   * are kept apart too, by consignment (#150); every other change is keyed by its column, as the
   * PDF keys it. The date shown above the envíos is the document's, which is envío 1's, so another
   * envío's date change is not under it: a document date amended on envíos that printed different
   * dates is one row per envío, and keyed by column the last of them won. When the envíos print
   * different dates each one's is shown in its block instead, with its own row (#135).
   */
  const { lastChanged, lastChangedNotes, lastChangedDates } = useMemo(() => {
    const entries = history.data?.data ?? []
    if (entries.length === 0 || !deca) {
      return {
        lastChanged: new Map<string, HistoryEntry>(),
        lastChangedNotes: new Map<number, HistoryEntry>(),
        lastChangedDates: new Map<number, HistoryEntry>(),
      }
    }
    const latest = Math.max(...entries.map((entry) => entry.versionNo))
    const newest = entries.filter((entry) => entry.versionNo === latest)
    const isNotes = (entry: HistoryEntry) => entry.field === 'observaciones' && entry.consignmentSeq != null
    const isEnviosDate = (entry: HistoryEntry) => entry.field === 'fecha_realizacion' && entry.consignmentSeq != null
    const isOtherEnviosDate = (entry: HistoryEntry) => isEnviosDate(entry) && entry.consignmentSeq !== 1
    return {
      lastChanged: new Map(
        newest.filter((entry) => !isNotes(entry) && !isOtherEnviosDate(entry)).map((entry) => [entry.field, entry]),
      ),
      lastChangedNotes: new Map(newest.filter(isNotes).map((entry) => [entry.consignmentSeq as number, entry])),
      lastChangedDates: new Map(newest.filter(isEnviosDate).map((entry) => [entry.consignmentSeq as number, entry])),
    }
  }, [history.data, deca])

  async function runAction(action: () => Promise<unknown>, message: string) {
    setBusy(true)
    setActionError(null)
    try {
      await action()
      invalidate(decaId)
      toast.success(message)
    } catch (error) {
      setActionError(isConflict(error) ? t('modify.versionConflict') : apiErrorMessage(error, t))
    } finally {
      setBusy(false)
    }
  }

  if (query.isLoading) {
    return (
      <AppShell title={t('detail.draftTitle')}>
        <Card>
          <LoadingRows rows={6} columns={3} />
        </Card>
      </AppShell>
    )
  }

  // A document that is **not there** is not a load failure (#17). `ErrorState` announces an
  // incident — `role="alert"`, the red icon, "No se han podido cargar los datos" — and offers a
  // *Reintentar* that cannot ever succeed, because nothing about the document is going to change.
  // `isResourceNotFound` is what keeps this off the API's bare `NOT_FOUND`, which means "no such
  // route" and is a genuine failure worth retrying.
  if (isResourceNotFound(query.error)) {
    return (
      <AppShell title={t('notFound.decaTitle')}>
        <Card>
          <EmptyState
            icon="search"
            title={t('notFound.decaTitle')}
            body={t('notFound.decaBody')}
            action={
              <Button variant="primary" onClick={() => void navigate({ to: '/decas' })}>
                {t('notFound.decaAction')}
              </Button>
            }
          />
        </Card>
      </AppShell>
    )
  }

  if (query.error || !deca) {
    return (
      <AppShell title={t('detail.draftTitle')}>
        <Card>
          <ErrorState error={query.error} onRetry={() => void query.refetch()} />
        </Card>
      </AppShell>
    )
  }

  const generated = isGenerated(deca)
  // Envíos that run on different days show each one's date in its block, as the PDF prints it under
  // each envío's row, rather than envío 1's as the document's (#135).
  const datesDiffer = consignmentDatesDiffer(deca)
  // A rejection here used to go nowhere — `void` on an uncaught promise — so a failed
  // download was a button that did nothing at all. Say so instead.
  const downloadPdf = () => {
    void downloadFile(
      `/api/v1/decas/${encodeURIComponent(deca.id)}/pdf`,
      MIME.pdf,
      `${deca.documentNumber ?? 'DeCA'}.pdf`,
    ).catch((error: unknown) => toast.error(apiErrorMessage(error, t)))
  }

  // The four secondary actions are each reachable twice — a button or a link on a desktop, an item
  // of «Más acciones» on a phone (#111). The three that act each have one body both call;
  // «Duplicar» only navigates, so its link and its item name the same route, and the tests pin
  // both. `busy` is checked here, not passed to the menu as `disabled`: `RowMenu` drops a disabled
  // item instead of greying it.
  const mayComplete = canComplete(deca) && abilities.writeDecas
  const mayVoid = canVoid(deca) && abilities.voidDecas
  const mayDeleteDraft = canDeleteDraft(deca) && abilities.writeDecas
  // #178 — in every status: the copy is a new document and this one is only read, so no status
  // rule applies. `writeDecas` alone, as for creating.
  // #220: and only for a company offered goods DeCAs.
  const mayDuplicate = abilities.writeDecas && offeredFamilies(principal).goods
  // #219 — «Enviar por correo»: a document whose link serves, by whoever may write documents.
  const maySend = canSendByEmail(deca) && abilities.writeDecas
  const complete = () => {
    if (busy) return
    void runAction(
      () =>
        unwrap(
          api.POST('/api/v1/decas/{decaId}/complete', {
            params: { path: { decaId: deca.id }, header: ifMatch(etag) },
          }),
        ),
      t('detail.completed'),
    )
  }
  const deleteDraft = () => {
    if (busy) return
    if (!window.confirm(t('detail.deleteDraftConfirm'))) return
    void runAction(
      () =>
        unwrap(
          api.DELETE('/api/v1/decas/{decaId}', {
            params: { path: { decaId: deca.id } },
          }),
        ),
      t('detail.deleted'),
    ).then(() => navigate({ to: '/decas', search: {} }))
  }

  return (
    <AppShell
      title={
        <span className="doc-number">
          {generated ? deca.documentNumber : t('detail.draftTitle')}
        </span>
      }
      // Whose document this is — for a role that can be inside more than one company, and only
      // for such a role. The parties on a DeCA are the *cargador* and the *transportista*, which
      // is a different question from which tenant issued it: a support agent arriving from a
      // cross-company list has no other way to tell, and everything they do next is done to this
      // company. For a tenant user there is only ever one answer, so it is noise.
      subtitle={
        abilities.choosesOrganisation && context
          ? t('detail.organisation', { organisation: context.name })
          : undefined
      }
    >
      {/* The status, the provenance and the three actions on one line above the
          sections — the frame's own arrangement. They were in the TopBar, which
          now also carries the global search and "+ Crear DeCA". */}
      <div className="detail-actions">
        <EstadoChip estado={deca.estado} voidReason={deca.voidReason} />
        {deca.isGrouped ? <GroupedTag count={deca.consignments.length} /> : null}
        <span className="small muted">
          {generated
            ? `${t('common.generatedAt')} ${formatDateTime(deca.generatedAt, i18n.language)} · ${t('common.version')} ${deca.versionNo}`
            : `${t('common.createdAt')} ${formatDateTime(deca.createdAt, i18n.language)}`}
        </span>

        <span className="spacer" />

        {/* The buttons wrap as one block. Left as individual flex items, a
            single overflowing action dropped onto a line by itself. On a phone the
            secondary actions are hidden and «Más acciones» offers them instead (#111):
            the primary action and the download stay one tap away. */}
        <div className="row wrap detail-actions__buttons" style={{ gap: 'var(--space-sm)' }}>
        {/* #90 — the way into the editor. Before this, a draft opened read-only with *Generar*
            and *Eliminar borrador* only, which is what pushed the client's testers into
            generating a document in order to be allowed to finish it. Guarded by `!generated`
            for the same reason the editor redirects: a generated DeCA changes through
            *Modificar* (BOE Fifth.1) or *Regenerar* (Fifth.2) and by no other means. */}
        {!generated && abilities.writeDecas ? (
          <Link to="/decas/$decaId/editar" params={{ decaId: deca.id }} className="btn btn--ghost">
            {t('common.edit')}
          </Link>
        ) : null}

        {!generated && abilities.writeDecas ? (
          <Button
            variant="primary"
            icon="check"
            disabled={busy}
            onClick={() =>
              void runAction(
                () =>
                  unwrap(
                    api.POST('/api/v1/decas/{decaId}/generate', {
                      params: { path: { decaId: deca.id }, header: ifMatch(etag) },
                    }),
                  ),
                t('create.generated', { documentNumber: deca.documentNumber ?? '' }),
              )
            }
          >
            {t('detail.generate')}
          </Button>
        ) : null}

        {canAmend(deca) && abilities.writeDecas ? (
          <Button variant="primary" icon="edit" onClick={() => setModifyOpen(true)}>
            {t('detail.modify')}
          </Button>
        ) : null}

        {authenticatedDownloadAvailable(deca) ? (
          <Button icon="download" onClick={downloadPdf}>
            {t('detail.downloadPdf')}
          </Button>
        ) : null}

        {/* Not in the frame, and it must stay: `complete` is in the contract and
            it is what starts the download clock. */}
        {mayComplete ? (
          <Button
            className="detail-actions__secondary"
            icon="checkCircle"
            disabled={busy}
            onClick={complete}
          >
            {t('detail.complete')}
          </Button>
        ) : null}

        {/* #178 — «Duplicar». A `Link`, as «Editar» is: it opens another screen, so it has to be
            middle-clickable and announced as a link. Secondary, so on a phone it is in «Más
            acciones» and the row that stays visible does not grow. */}
        {mayDuplicate ? (
          <Link
            to="/decas/$decaId/duplicar"
            params={{ decaId: deca.id }}
            className="btn btn--secondary detail-actions__secondary"
          >
            <Icon name="copy" size={16} />
            {t('duplicate.action')}
          </Link>
        ) : null}

        {maySend ? (
          <Button className="detail-actions__secondary" icon="mail" onClick={() => setSendOpen(true)}>
            {t('detail.sendEmail')}
          </Button>
        ) : null}

        {mayVoid ? (
          <Button
            className="detail-actions__secondary"
            variant="danger"
            icon="ban"
            onClick={() => setVoidOpen(true)}
          >
            {t('detail.void')}
          </Button>
        ) : null}

        {mayDeleteDraft ? (
          <Button
            className="detail-actions__secondary"
            variant="danger"
            icon="trash"
            disabled={busy}
            onClick={deleteDraft}
          >
            {t('detail.deleteDraft')}
          </Button>
        ) : null}

        {/* The same secondary actions, for a phone. Hidden above 600 px by CSS; its items exist only
            while it is open, so the desktop DOM gains one closed trigger and no second
            copy of any action. */}
        {mayComplete || mayDuplicate || maySend || mayVoid || mayDeleteDraft ? (
          <div className="detail-actions__more">
            <RowMenu
              label={t('detail.moreActions')}
              items={[
                {
                  key: 'complete',
                  label: t('detail.complete'),
                  icon: 'checkCircle',
                  disabled: !mayComplete,
                  onSelect: complete,
                },
                {
                  key: 'duplicate',
                  label: t('duplicate.action'),
                  icon: 'copy',
                  disabled: !mayDuplicate,
                  onSelect: () =>
                    void navigate({ to: '/decas/$decaId/duplicar', params: { decaId: deca.id } }),
                },
                {
                  key: 'sendEmail',
                  label: t('detail.sendEmail'),
                  icon: 'mail',
                  disabled: !maySend,
                  onSelect: () => setSendOpen(true),
                },
                {
                  key: 'void',
                  label: t('detail.void'),
                  icon: 'ban',
                  danger: true,
                  disabled: !mayVoid,
                  onSelect: () => setVoidOpen(true),
                },
                {
                  key: 'deleteDraft',
                  label: t('detail.deleteDraft'),
                  icon: 'trash',
                  danger: true,
                  disabled: !mayDeleteDraft,
                  onSelect: deleteDraft,
                },
              ]}
            />
          </div>
        ) : null}
        </div>
      </div>

      {actionError ? <Notice tone="danger">{actionError}</Notice> : null}

      {deca.supersededByDocumentId ? (
        <Notice tone="warning">
          {t('estado.supersededBy', { documentNumber: '' })}{' '}
          <Link to="/decas/$decaId" params={{ decaId: deca.supersededByDocumentId }}>
            {deca.supersededByDocumentId}
          </Link>
        </Notice>
      ) : null}
      {deca.supersedesDocumentId ? (
        <Notice tone="info">
          {t('estado.supersedes', { documentNumber: '' })}{' '}
          <Link to="/decas/$decaId" params={{ decaId: deca.supersedesDocumentId }}>
            {deca.supersedesDocumentId}
          </Link>
        </Notice>
      ) : null}

      {/* A generated document leads with its QR card in the DOM, so a phone, a screen reader
          and Tab reach the QR and the download first (#111); `detail-layout--qr-first` then
          places it back in the right-hand column on a desktop. A draft's card holds only a
          notice, so a draft keeps it in the aside. */}
      <div className={generated ? 'detail-layout detail-layout--qr-first' : 'detail-layout'}>
        {generated ? (
          <div className="detail-layout__qr">
            <Card title={t('detail.qrTitle')}>
              <QrPanel
                qrPath={`/api/v1/decas/${encodeURIComponent(deca.id)}/qr?format=png&size=512`}
                documentNumber={deca.documentNumber}
                directDownloadUrl={deca.directDownloadUrl}
                csv={deca.csv}
                live={directDownloadLive(deca)}
                expiresLabel={
                  deca.directDownloadExpiresAt
                    ? t('detail.expiresAt', {
                        date: formatDateTime(deca.directDownloadExpiresAt, i18n.language),
                      })
                    : undefined
                }
                onDownloadPdf={downloadPdf}
              />
            </Card>
          </div>
        ) : null}

        <div className="stack detail-layout__main">
          <div className="detail-parties">
            <Card
              title={
                <>
                  <SectionNumber n={1} />
                  {t('sections.cargador')}
                </>
              }
              id="s-cargador"
            >
            <div className="datum-grid">
              {fieldsForSection('cargador').map((field) => (
                <Datum
                  key={field.column}
                  label={t(`fields.${field.column}.label`)}
                  value={deca[field.column as keyof Deca] as string}
                  modified={lastChanged.has(field.column)}
                  previousValue={lastChanged.get(field.column)?.oldValue ?? null}
                />
              ))}
            </div>
            </Card>

            <Card
              title={
                <>
                  <SectionNumber n={2} />
                  {t('sections.transportista')}
                </>
              }
              id="s-transportista"
            >
            <div className="datum-grid">
              {fieldsForSection('transportista').map((field) => (
                <Datum
                  key={field.column}
                  label={t(`fields.${field.column}.label`)}
                  value={deca[field.column as keyof Deca] as string}
                  modified={lastChanged.has(field.column)}
                  previousValue={lastChanged.get(field.column)?.oldValue ?? null}
                />
              ))}
            </div>
            </Card>
          </div>

          <Card
            title={
              <>
                <SectionNumber n={3} />
                {t('sections.envio')}
              </>
            }
            subtitle={t('detail.consignmentsCount', { count: deca.consignments.length })}
            id="s-envio"
          >
            <div className="stack">
              {datesDiffer ? null : (
                <div className="datum-grid">
                  <Datum
                    label={t('fields.fecha_realizacion.label')}
                    value={formatServiceDate(deca.fecha_realizacion, deca.fechaRealizacionHasTime, i18n.language)}
                    tabular
                    modified={lastChanged.has('fecha_realizacion')}
                    previousValue={lastChanged.get('fecha_realizacion')?.oldValue ?? null}
                    previousHasTime={lastChanged.get('fecha_realizacion')?.oldHasTime}
                  />
                </div>
              )}

              {deca.consignments.map((row) => {
                const date = consignmentServiceDate(row, deca)
                const dateChange = lastChangedDates.get(row.seq)
                return (
                  <div className="consignment" key={row.seq}>
                    <div className="consignment__header">
                      <span className="consignment__seq">
                        <span className="consignment__badge">{row.seq}</span>
                        {t('create.consignment', { seq: row.seq })}
                      </span>
                    </div>
                    <div className="datum-grid">
                      <Datum
                        label={t('fields.origen_domicilio.label')}
                        value={row.origen_domicilio}
                      />
                      <Datum
                        label={t('fields.destino_domicilio.label')}
                        value={row.destino_domicilio}
                      />
                      {/* Field 9, in the dictionary's place. */}
                      {datesDiffer ? (
                        <Datum
                          label={t('fields.fecha_realizacion.label')}
                          value={formatServiceDate(date.iso, date.hasTime, i18n.language)}
                          tabular
                          modified={dateChange != null}
                          previousValue={dateChange?.oldValue ?? null}
                          previousHasTime={dateChange?.oldHasTime}
                        />
                      ) : null}
                      <Datum
                        label={t('fields.naturaleza_mercancia.label')}
                        value={row.naturaleza_mercancia}
                      />
                      <Datum
                        label={t('fields.peso_magnitud.label')}
                        value={formatQuantity(row.peso_magnitud, row.unidad_medida, i18n.language)}
                        tabular
                      />
                      {row.observaciones?.trim() ? (
                        <Datum
                          label={t('fields.observaciones.label')}
                          value={row.observaciones}
                          modified={lastChangedNotes.has(row.seq)}
                          previousValue={lastChangedNotes.get(row.seq)?.oldValue ?? null}
                        />
                      ) : null}
                    </div>
                  </div>
                )
              })}

              {deca.isGrouped ? <p className="small muted">{t('group.orderMeaning')}</p> : null}
            </div>
          </Card>

          <Card
            title={
              <>
                <SectionNumber n={4} />
                {t('sections.transporte')}
              </>
            }
            id="s-transporte"
          >
            <div className="datum-grid">
              {fieldsForSection('transporte').map((field) => (
                <Datum
                  key={field.column}
                  label={
                    <>
                      {t(`fields.${field.column}.label`)}
                      {!field.onPdf ? (
                        <span className="muted"> · {t('create.notPrinted')}</span>
                      ) : null}
                    </>
                  }
                  value={
                    field.column === 'matricula_remolque' && deca.rigido
                      ? t('fields.matricula_remolque.rigidoPrinted')
                      : (deca[field.column as keyof Deca] as string)
                  }
                  modified={lastChanged.has(field.column)}
                  previousValue={lastChanged.get(field.column)?.oldValue ?? null}
                />
              ))}
            </div>
          </Card>

          <Card
            title={
              <>
                <SectionNumber n={5} />
                {t('detail.historial')}
              </>
            }
            subtitle={t('detail.versionsHelp')}
            id="s-historial"
            flush
          >
            {(history.data?.data ?? []).length === 0 ? (
              <p className="small muted" style={{ padding: 'var(--space-xl)' }}>
                {t('detail.historialEmpty')}
              </p>
            ) : (
              <div className="table-wrap">
                <table className="table table--stack">
                  <caption className="sr-only">{t('detail.historial')}</caption>
                  <thead>
                    <tr>
                      <th scope="col">{t('detail.historialColumns.changedAt')}</th>
                      <th scope="col">{t('detail.historialColumns.field')}</th>
                      <th scope="col">{t('detail.historialColumns.description')}</th>
                      <th scope="col">{t('detail.historialColumns.usuario')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(history.data?.data ?? []).map((entry, index) => (
                      <tr key={`${entry.versionNo}-${entry.field}-${index}`}>
                        <td className="tabular" data-label={t('detail.historialColumns.changedAt')}>
                          {formatDateTime(entry.changedAt, i18n.language)}
                        </td>
                        <td data-label={t('detail.historialColumns.field')}>
                          {entry.fieldLabelEs}
                          {entry.consignmentSeq != null ? (
                            <span className="muted">
                              {' '}
                              · {t('create.consignment', { seq: entry.consignmentSeq })}
                            </span>
                          ) : null}
                          <div className="small muted">
                            <s>{formatHistoryValue(entry.oldValue, i18n.language, entry.oldHasTime) ?? '—'}</s> →{' '}
                            {formatHistoryValue(entry.newValue, i18n.language, entry.newHasTime) ?? '—'}
                          </div>
                        </td>
                        <td data-label={t('detail.historialColumns.description')}>
                          {entry.descriptionEs}
                        </td>
                        {/* The typed actor, not `usuario`: the same string, but only the type says it
                            came from a linked phone (#110). */}
                        <td data-label={t('detail.historialColumns.usuario')}>
                          {actorText(entry.actor, t)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </div>

        <aside className="stack detail-layout__aside">
          {!generated ? (
            <Card title={t('detail.qrTitle')}>
              <Notice tone="neutral">{t('create.railHelp')}</Notice>
            </Card>
          ) : null}

          {/* One "Detalles" card, as the frame has it, replacing the separate
              Versiones and Creado-por cards. The version *chain* is not lost:
              section ⑤ lists every change with its author and timestamp. The
              reason is not among its columns — it comes only from /versions,
              and it is printed on the PDF (#120). */}
          <Card title={t('detail.details')}>
            <div className="stack-sm small">
              <div className="row-between">
                <span className="muted">{t('list.columns.estado')}</span>
                <EstadoChip estado={deca.estado} voidReason={deca.voidReason} />
              </div>
              <Datum
                label={t('detail.type')}
                value={
                  deca.isGrouped
                    ? t('detail.typeGrouped', { count: deca.consignments.length })
                    : t('detail.typeSingle')
                }
              />
              <Datum
                label={t('common.createdBy')}
                value={
                  deca.createdBy
                    ? `${actorText(deca.createdBy, t)} · ${formatDateTime(deca.createdAt, i18n.language)}`
                    : undefined
                }
              />
              {/* Addendum No. 3, Point 7. The line is for administrators alone (design spec §10): every
                  other role is handed no owner to show, although the body carries one for any reader. */}
              <DocumentOwnerLine
                owner={abilities.administer ? deca.owner : null}
                subject={deca}
                canReassign={abilities.administer}
                reassign={(userId) =>
                  unwrap(
                    api.POST('/api/v1/decas/{decaId}/reassign', {
                      params: { path: { decaId: deca.id } },
                      body: { userId },
                    }),
                  )
                }
                onReassigned={() => {
                  invalidate(deca.id)
                  toast.success(t('owner.reassigned'))
                }}
              />
              {lastVersion ? (
                <Datum
                  label={t('detail.modifiedBy')}
                  // `createdBy` is required in the contract, yet the serialiser can send null for a
                  // version with no recorded display. The dash keeps the date readable either way.
                  value={`${lastVersion.createdBy ? actorText(lastVersion.createdBy, t) : '—'} · ${formatDateTime(lastVersion.createdAt, i18n.language)}`}
                />
              ) : null}
              <Datum
                label={t('detail.directDownload')}
                value={t('detail.days', { count: downloadWindowDays(principal) })}
                hint={
                  deca.directDownloadExpiresAt
                    ? t('detail.expiresAt', {
                        date: formatDateTime(deca.directDownloadExpiresAt, i18n.language),
                      })
                    : undefined
                }
              />
              {/*
                The date, not a window. `retainUntil` is computed from the organisation's own
                `retentionMonths` and is what the retention sweep enforces; the portal used to
                print a hardcoded "24 meses" beside it, which was right only for a client on the
                default and said nothing the date did not already say.
              */}
              <Datum
                label={t('detail.retention')}
                value={
                  deca.retainUntil
                    ? t('detail.retainUntil', { date: formatDateTime(deca.retainUntil, i18n.language) })
                    : t('common.none')
                }
              />
              {deca.externalRef ? (
                <Datum label={t('fields.externalRef.label')} value={deca.externalRef} />
              ) : null}
              {deca.pdf ? (
                <Datum label="PDF" value={`${Math.round((deca.pdf.bytes ?? 0) / 1024)} kB`} tabular />
              ) : null}
            </div>
          </Card>

          {/* #160: what drivers confirmed from the DAC app. Not on a draft: only an issued document
              can be found on the road, so a draft has nothing to confirm. On a phone the aside
              stacks after the sections, and this card with it. */}
          {generated ? (
            <Card title={t('delivery.title')} id="s-entrega">
              <DeliveryPanel deca={deca} administer={abilities.administer} />
            </Card>
          ) : null}

          {/* #219: every sending of the document by email, and what became of each letter. */}
          {generated ? (
            <Card title={t('emails.title')} id="s-correo">
              <DecaEmailsPanel deca={deca} onSend={maySend ? () => setSendOpen(true) : undefined} />
            </Card>
          ) : null}
        </aside>
      </div>

      {modifyOpen ? (
        <ModifyDialog deca={deca} etag={etag} open={modifyOpen} onClose={() => setModifyOpen(false)} />
      ) : null}

      {sendOpen ? (
        <EmailSendDialog
          deca={deca}
          onClose={() => setSendOpen(false)}
          onSent={(count) => {
            setSendOpen(false)
            invalidate(decaId)
            toast.success(t('emails.sent', { count }))
          }}
        />
      ) : null}

      <VoidDialog
        deca={deca}
        etag={etag}
        open={voidOpen}
        onClose={() => setVoidOpen(false)}
        onDone={() => {
          invalidate(decaId)
          toast.success(t('detail.voided'))
        }}
      />
    </AppShell>
  )
}

function VoidDialog({
  deca,
  etag,
  open,
  onClose,
  onDone,
}: {
  deca: Deca
  etag: string | null
  open: boolean
  onClose: () => void
  onDone: () => void
}) {
  const { t } = useTranslation()
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function submit() {
    if (reason.trim().length < 3) {
      setError(t('errors.required'))
      return
    }
    setBusy(true)
    setError(null)
    try {
      await unwrap(
        api.POST('/api/v1/decas/{decaId}/void', {
          params: { path: { decaId: deca.id }, header: ifMatch(etag) },
          body: { reason: reason.trim() },
        }),
      )
      onDone()
      onClose()
    } catch (caught) {
      setError(isConflict(caught) ? t('modify.versionConflict') : apiErrorMessage(caught, t))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog
      open={open}
      busy={busy}
      title={t('modify.voidTitle', { documentNumber: deca.documentNumber ?? '' })}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose} disabled={busy}>
            {t('common.cancel')}
          </Button>
          <Button variant="danger" icon="ban" loading={busy} disabled={busy} onClick={() => void submit()}>
            {t('modify.voidSubmit')}
          </Button>
        </>
      }
    >
      <p className="small">{t('modify.voidIntro')}</p>
      <Notice tone="warning">{t('modify.voidWarning')}</Notice>
      {/* #160: a delivered document is an odd one to cancel, and what drivers sent stays with it. */}
      {hasDeliveryConfirmation(deca) ? <Notice tone="warning">{t('modify.voidDelivered')}</Notice> : null}
      {error ? <Notice tone="danger">{error}</Notice> : null}
      <TextAreaInput
        label={t('modify.voidReason')}
        required
        rows={3}
        value={reason}
        onChange={(event) => setReason(event.target.value)}
      />
    </Dialog>
  )
}
